import React, { useEffect, useState, useCallback } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Settings, DollarSign, FileText, Check, X, RotateCcw, Plus, Play, Send, Download } from "lucide-react";

function fmtMxn(n) { return `$${(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }

export default function SuperadminAfiliados() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [tab, setTab] = useState("affiliates");
  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-widest text-brand-orange-500 font-semibold">Fase 14.4 · Afiliados</p>
          <h1 className="mt-2 text-3xl font-bold text-white font-display flex items-center gap-2">
            <Users className="w-7 h-7" /> Programa de afiliados
          </h1>
        </div>
        <div className="border-b border-gray-800 mb-6 flex gap-4">
          {[
            { v: "affiliates", l: "Afiliados", i: Users },
            { v: "rules", l: "Reglas", i: Settings },
            { v: "commissions", l: "Comisiones", i: DollarSign },
            { v: "statements", l: "Statements", i: FileText },
          ].map((t) => (
            <button key={t.v} onClick={() => setTab(t.v)}
              data-testid={`afiliados-tab-${t.v}`}
              className={`py-3 px-1 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
                tab === t.v ? "border-brand-orange-600 text-white" : "border-transparent text-gray-500 hover:text-gray-300"
              }`}>
              <t.i className="w-4 h-4" /> {t.l}
            </button>
          ))}
        </div>
        {tab === "affiliates" && <AffiliatesTab />}
        {tab === "rules" && <RulesTab />}
        {tab === "commissions" && <CommissionsTab />}
        {tab === "statements" && <StatementsTab />}
      </div>
    </StaffLayout>
  );
}

function AffiliatesTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/affiliates").then((r) => setItems(r.data.items || [])); }, []);
  return (
    <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="affiliates-list-table">
      <table className="w-full">
        <thead className="bg-[#0B0F14] border-b border-gray-800">
          <tr>
            <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Afiliado</th>
            <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Rol</th>
            <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Links</th>
            <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Pending</th>
            <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Approved</th>
            <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Paid</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {items.map((a) => (
            <tr key={a.id} data-testid={`affiliate-row-${a.email}`}>
              <td className="px-4 py-2">
                <div className="text-sm text-white">{a.full_name}</div>
                <div className="text-xs font-mono text-gray-500">{a.email}</div>
              </td>
              <td className="px-4 py-2">
                <span className="inline-flex px-2 py-0.5 text-[10px] rounded border border-brand-orange-500/40 text-brand-orange-500 bg-brand-orange-500/10 uppercase tracking-widest font-semibold">
                  {(a.roles || []).find((r) => r === "reseller" || r === "influencer") || "—"}
                </span>
              </td>
              <td className="px-4 py-2 text-right text-sm font-mono text-white">{a.links_count}</td>
              <td className="px-4 py-2 text-right text-sm font-mono text-yellow-300">{fmtMxn(a.commissions?.pending_mxn)}</td>
              <td className="px-4 py-2 text-right text-sm font-mono text-blue-300">{fmtMxn(a.commissions?.approved_mxn)}</td>
              <td className="px-4 py-2 text-right text-sm font-mono text-emerald-300">{fmtMxn(a.commissions?.paid_mxn)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RulesTab() {
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => api.get("/admin/commission-rules").then((r) => setItems(r.data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta regla?")) return;
    try { await api.delete(`/admin/commission-rules/${id}`); toast.success("Regla desactivada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-gray-400">{items.length} reglas · match por priority (mayor gana)</p>
        <button onClick={() => setShowNew(true)}
          data-testid="new-rule-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">
          <Plus className="w-3 h-3" /> Nueva regla
        </button>
      </div>
      <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="rules-table">
        <table className="w-full">
          <thead className="bg-[#0B0F14] border-b border-gray-800">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Prio</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Nombre</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Rol</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Scope</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Comisión</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Ejemplo</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Estado</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-widest"> </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {items.map((r) => (
              <tr key={r.id} data-testid={`rule-row-${r.id}`}>
                <td className="px-4 py-2 text-sm font-mono text-brand-orange-500 font-bold">{r.priority}</td>
                <td className="px-4 py-2 text-sm text-white">{r.name}</td>
                <td className="px-4 py-2 text-xs font-mono text-gray-300">{r.affiliate_role || "—"}</td>
                <td className="px-4 py-2 text-xs font-mono text-gray-400">
                  {r.scope}
                  {r.target_slugs?.length > 0 && <span className="ml-1 text-[10px] text-brand-orange-500">[{r.target_slugs.join(",")}]</span>}
                </td>
                <td className="px-4 py-2 text-xs font-mono text-white">
                  {r.commission_type === "percentage" ? `${r.commission_value}%` : `$${r.commission_value} flat`}
                </td>
                <td className="px-4 py-2 text-[10px] text-gray-400">
                  {r.commission_type === "percentage" ? `Order $80k → ${fmtMxn(80000 * r.commission_value / 100)}` : "—"}
                </td>
                <td className="px-4 py-2">
                  {r.is_active ? <Check className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-red-400" />}
                </td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => del(r.id)} className="p-1 text-gray-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showNew && <NewRuleModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewRuleModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", affiliate_role: "reseller", scope: "all", target_slugs: "",
    commission_type: "percentage", commission_value: 15, priority: 1,
  });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, commission_value: parseFloat(form.commission_value), priority: parseInt(form.priority),
        target_slugs: form.target_slugs ? form.target_slugs.split(",").map(s => s.trim()) : [] };
      await api.post("/admin/commission-rules", body);
      toast.success("Regla creada");
      onCreated();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3" data-testid="new-rule-modal">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-bold text-white font-display">Nueva regla</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Nombre" className="w-full bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" data-testid="rule-form-name" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.affiliate_role} onChange={(e) => setForm({ ...form, affiliate_role: e.target.value })}
            className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" data-testid="rule-form-role">
            <option value="reseller">Reseller</option>
            <option value="influencer">Influencer</option>
          </select>
          <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}
            className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" data-testid="rule-form-scope">
            <option value="all">All</option>
            <option value="program">Program</option>
            <option value="subscription">Subscription</option>
            <option value="bundle">Bundle</option>
          </select>
        </div>
        {form.scope !== "all" && (
          <input value={form.target_slugs} onChange={(e) => setForm({ ...form, target_slugs: e.target.value })}
            placeholder="target_slugs (csv)" className="w-full bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white font-mono" />
        )}
        <div className="grid grid-cols-3 gap-2">
          <select value={form.commission_type} onChange={(e) => setForm({ ...form, commission_type: e.target.value })}
            className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white">
            <option value="percentage">%</option>
            <option value="flat_mxn">Flat MXN</option>
          </select>
          <input type="number" value={form.commission_value} onChange={(e) => setForm({ ...form, commission_value: e.target.value })}
            className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white font-mono" placeholder="Valor" data-testid="rule-form-value" />
          <input type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
            className="bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white font-mono" placeholder="Prio" />
        </div>
        <button type="submit" data-testid="rule-form-submit"
          className="w-full py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold">Crear</button>
      </form>
    </div>
  );
}

function CommissionsTab() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    const p = filter ? `?status=${filter}` : "";
    api.get(`/admin/commissions${p}&limit=50`).then((r) => setItems(r.data.items || []));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => {
    try { await api.post(`/admin/commissions/${id}/approve`); toast.success("Aprobada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const reverse = async (id) => {
    const reason = window.prompt("Razón de la reversión:");
    if (!reason) return;
    try { await api.post(`/admin/commissions/${id}/reverse`, { reason }); toast.success("Reversada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div>
      <div className="flex gap-2 mb-3">
        {["", "pending", "approved", "paid", "reversed"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)}
            data-testid={`commissions-filter-${s || "all"}`}
            className={`px-3 py-1 text-xs rounded border ${filter === s ? "bg-brand-orange-600 text-white border-brand-orange-600" : "border-gray-700 text-gray-400"}`}>
            {s || "todos"}
          </button>
        ))}
      </div>
      <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="admin-commissions-table">
        <table className="w-full">
          <thead className="bg-[#0B0F14] border-b border-gray-800">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Fecha</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Afiliado</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Orden</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Monto</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Comisión</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {items.map((c) => (
              <tr key={c.id} data-testid={`commission-row-${c.id}`}>
                <td className="px-4 py-2 text-xs text-gray-400 font-mono">{(c.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-2 text-xs font-mono text-gray-300">{(c.affiliate_user_id || "").slice(0, 8)}</td>
                <td className="px-4 py-2 text-xs font-mono text-white">{c.order_id || c.subscription_id || "—"}</td>
                <td className="px-4 py-2 text-right text-xs font-mono">{fmtMxn(c.order_amount_mxn)}</td>
                <td className="px-4 py-2 text-right text-xs font-mono text-brand-orange-500">{fmtMxn(c.commission_amount_mxn)}</td>
                <td className="px-4 py-2"><span className={`px-2 py-0.5 text-[10px] uppercase rounded border ${
                  c.status === "pending" ? "bg-yellow-500/20 text-yellow-300 border-yellow-500/40" :
                  c.status === "approved" ? "bg-blue-500/20 text-blue-300 border-blue-500/40" :
                  c.status === "paid" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" :
                  "bg-red-500/20 text-red-300 border-red-500/40"
                }`}>{c.status}</span></td>
                <td className="px-4 py-2 text-right">
                  {c.status === "pending" && (
                    <button onClick={() => approve(c.id)} data-testid={`commission-approve-${c.id}`}
                      className="p-1 text-gray-400 hover:text-emerald-500" title="Aprobar"><Check className="w-4 h-4" /></button>
                  )}
                  {(c.status === "pending" || c.status === "approved") && (
                    <button onClick={() => reverse(c.id)} data-testid={`commission-reverse-${c.id}`}
                      className="p-1 text-gray-400 hover:text-red-500" title="Reversar"><RotateCcw className="w-4 h-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatementsTab() {
  const [items, setItems] = useState([]);
  const [showGen, setShowGen] = useState(false);
  const load = useCallback(() => api.get("/admin/statements?limit=50").then((r) => setItems(r.data.items || [])), []);
  useEffect(() => { load(); }, [load]);
  const send = async (id) => { try { await api.post(`/admin/statements/${id}/send`); toast.success("Enviado"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const markPaid = async (id) => {
    const ref = window.prompt("Referencia de pago (folio SPEI/CLABE/PayPal):");
    if (!ref) return;
    try { await api.post(`/admin/statements/${id}/mark-paid`, { payment_reference: ref, payment_method: "spei" }); toast.success("Marcado como pagado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const downloadPdf = async (id) => {
    try {
      const resp = await api.get(`/affiliate/statements/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(resp.data), "_blank");
    } catch (e) { toast.error("Error al descargar"); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-gray-400">{items.length} statements</p>
        <button onClick={() => setShowGen(true)}
          data-testid="generate-statement-btn"
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white text-sm font-semibold">
          <Plus className="w-3 h-3" /> Generar mensual
        </button>
      </div>
      <div className="bg-[#111827] border border-gray-800 rounded-xl overflow-x-auto" data-testid="admin-statements-table">
        <table className="w-full">
          <thead className="bg-[#0B0F14] border-b border-gray-800">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Periodo</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Afiliado ID</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase"># Coms</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">NET</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Status</th>
              <th className="text-right px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {items.map((s) => (
              <tr key={s.id} data-testid={`statement-row-${s.id}`}>
                <td className="px-4 py-2 text-xs text-gray-300 font-mono">{(s.period_start || "").slice(0, 7)}</td>
                <td className="px-4 py-2 text-xs font-mono text-gray-400">{(s.affiliate_user_id || "").slice(0, 8)}</td>
                <td className="px-4 py-2 text-right text-xs font-mono text-white">{s.commissions_count}</td>
                <td className="px-4 py-2 text-right text-sm font-mono text-brand-orange-500 font-bold">{fmtMxn(s.net_mxn)}</td>
                <td className="px-4 py-2"><span className="px-2 py-0.5 text-[10px] rounded border uppercase font-semibold">{s.status}</span></td>
                <td className="px-4 py-2 text-right space-x-1">
                  <button onClick={() => downloadPdf(s.id)} className="p-1 text-gray-400 hover:text-brand-orange-500" title="Descargar PDF"><Download className="w-4 h-4" /></button>
                  {s.status === "draft" && <button onClick={() => send(s.id)} className="p-1 text-gray-400 hover:text-yellow-500" title="Enviar"><Send className="w-4 h-4" /></button>}
                  {(s.status === "draft" || s.status === "sent") && <button onClick={() => markPaid(s.id)} className="p-1 text-gray-400 hover:text-emerald-500" title="Marcar pagado"><Check className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showGen && <GenerateStatementModal onClose={() => setShowGen(false)} onDone={() => { setShowGen(false); load(); }} />}
    </div>
  );
}

function GenerateStatementModal({ onClose, onDone }) {
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [form, setForm] = useState({ period_start: monthStart, period_end: monthEnd });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/statements/generate", {
        period_start: form.period_start + "T00:00:00+00:00",
        period_end: form.period_end + "T23:59:59+00:00",
      });
      toast.success(`Generados: ${data.generated}`);
      onDone();
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
        className="w-full max-w-md bg-[#111827] border border-gray-800 rounded-xl p-6 space-y-3"
        data-testid="generate-statement-modal">
        <div className="flex items-start justify-between">
          <h3 className="text-xl font-bold text-white font-display">Generar statements</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X /></button>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Periodo desde</label>
          <input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })}
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-widest text-gray-400">Periodo hasta</label>
          <input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })}
            className="w-full mt-1 bg-[#0B0F14] border border-gray-800 rounded-md px-3 py-2 text-sm text-white" />
        </div>
        <p className="text-[11px] text-gray-500">Se generará un statement por afiliado con commissions <strong>approved</strong> en el periodo.</p>
        <button type="submit" data-testid="generate-statement-submit"
          className="w-full py-2 rounded-md bg-brand-orange-600 hover:bg-brand-orange-500 text-white font-semibold">Generar</button>
      </form>
    </div>
  );
}
